import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Dictionary } from 'lodash';
import {
  GetSampleTrackingCountsBySampleType,
  GetSampleTrackingCountsBySampleTypeForTransition,
  GetSampleTrackingCountsBySampleTypeForResearchProjects,
  SampleTrackingCounts,
} from '../data/SampleTrackingData';
import useAuth from '../auth/UseAuth';

interface SampleTrackingContextType {
  researchProjectCounts: Dictionary<ReadonlyArray<SampleTrackingCounts>>;
  getSampleTrackingCountsForProject: (
    researchProjectId: string,
    configuredTransitionId?: string
  ) => Promise<ReadonlyArray<SampleTrackingCounts>>;
  getSampleTrackingCountsForAllProjects: (researchProjectIds: string[]) => Promise<ReadonlyArray<SampleTrackingCounts>>;
  refreshSampleTrackingCountsForTransition: (
    researchProjectId: string,
    configuredTransitionId: string
  ) => Promise<ReadonlyArray<SampleTrackingCounts>>;
  refreshSampleTrackingCountsForProject: (researchProjectId: string) => Promise<ReadonlyArray<SampleTrackingCounts>>;
}

const SampleTrackingContext = createContext<SampleTrackingContextType | undefined>(undefined);

export const useSampleTrackingContext = () => {
  const context = useContext(SampleTrackingContext);
  if (!context) {
    throw new Error('useSampleTrackingContext must be used within a SampleTrackingProvider');
  }
  return context;
};

interface SampleTrackingProviderProps {
  children: ReactNode;
}

export const SampleTrackingProvider: React.FC<SampleTrackingProviderProps> = ({ children }) => {
  const { accessToken } = useAuth();
  const [researchProjectCounts, setResearchProjectCounts] = useState<Dictionary<ReadonlyArray<SampleTrackingCounts>>>(
    {}
  );

  // Updates the researchProjectCounts and returns the updated counts.
  const updateResearchProjectCounts = (
    researchProjectIds: string[],
    sampleTrackingCounts: ReadonlyArray<SampleTrackingCounts>
  ): Dictionary<ReadonlyArray<SampleTrackingCounts>> => {
    const countByResearchProjectId = sampleTrackingCounts.reduce(
      (dict: Record<string, SampleTrackingCounts[]>, count) => {
        dict[count.researchProjectId] || (dict[count.researchProjectId] = []);
        dict[count.researchProjectId].push(count);
        return dict;
      },
      {}
    );

    const newCounts = { ...researchProjectCounts };
    researchProjectIds.forEach(id => {
      if (!countByResearchProjectId[id]) {
        newCounts[id] = [];
      }
    });
    Object.entries(countByResearchProjectId).forEach(([id, counts]) => {
      newCounts[id] = counts;
    });

    setResearchProjectCounts(newCounts);
    return newCounts;
  };

  // Updates the researchProjectCounts for a transition and returns the updated counts.
  const updateResearchProjectCountsForTransition = (
    researchProjectId: string,
    sampleTrackingCounts: ReadonlyArray<SampleTrackingCounts>
  ): Dictionary<ReadonlyArray<SampleTrackingCounts>> => {
    const newCounts = { ...researchProjectCounts };
    const existingCounts = newCounts[researchProjectId] || [];

    if (!sampleTrackingCounts || sampleTrackingCounts.length === 0) {
      newCounts[researchProjectId] = [];
      return newCounts;
    }

    const existingCountsBySampleGroup = existingCounts.reduce((acc, count) => {
      acc[count.sampleGroup] = count;
      return acc;
    }, {} as Record<string, SampleTrackingCounts>);

    const updatedCounts = sampleTrackingCounts.map(count => {
      const sampleGroup = count.sampleGroup;

      if (existingCountsBySampleGroup[sampleGroup]) {
        const existingCount = existingCountsBySampleGroup[sampleGroup];

        return {
          ...existingCount,
          sampleCounts: { ...existingCount.sampleCounts, ...count.sampleCounts },
          patientCounts: { ...existingCount.patientCounts, ...count.patientCounts },
        };
      }

      return count;
    });

    const otherSampleGroups = existingCounts.filter(
      count => !sampleTrackingCounts.some(stc => stc.sampleGroup === count.sampleGroup)
    );

    newCounts[researchProjectId] = [...otherSampleGroups, ...updatedCounts];

    setResearchProjectCounts(newCounts);
    return newCounts;
  };

  // Fetches the sample tracking counts for a project and returns the counts.
  // Will check the cache first and only fetch the missing project if necessary.
  const getSampleTrackingCountsForProject = async (
    researchProjectId: string,
    configuredTransitionId?: string
  ): Promise<ReadonlyArray<SampleTrackingCounts>> => {
    if (researchProjectCounts[researchProjectId]) {
      return researchProjectCounts[researchProjectId];
    }

    return new Promise<ReadonlyArray<SampleTrackingCounts>>(resolve => {
      if (!accessToken) {
        resolve(researchProjectCounts[researchProjectId] || []);
        return;
      }
      (async () => {
        let fetchedData: ReadonlyArray<SampleTrackingCounts> = [];
        let updatedCounts: Dictionary<ReadonlyArray<SampleTrackingCounts>> = {};

        if (configuredTransitionId) {
          fetchedData = await GetSampleTrackingCountsBySampleTypeForTransition(
            researchProjectId,
            configuredTransitionId,
            accessToken
          );
          updatedCounts = updateResearchProjectCountsForTransition(researchProjectId, fetchedData);
        } else {
          fetchedData = await GetSampleTrackingCountsBySampleTypeForResearchProjects([researchProjectId], accessToken);
          updatedCounts = updateResearchProjectCounts([researchProjectId], fetchedData);
        }

        resolve(updatedCounts[researchProjectId]);
      })();
    });
  };

  // Fetches the sample tracking counts for all projects and returns the counts.
  // Will check the cache first and only fetch the missing projects if necessary.
  const getSampleTrackingCountsForAllProjects = async (
    researchProjectIds: string[]
  ): Promise<ReadonlyArray<SampleTrackingCounts>> => {
    const missingProjects = researchProjectIds.filter(rpId => !researchProjectCounts[rpId]);

    if (missingProjects.length === 0) {
      return researchProjectIds.flatMap(id => researchProjectCounts[id] || []);
    }

    return new Promise<ReadonlyArray<SampleTrackingCounts>>(resolve => {
      if (!accessToken) {
        resolve(researchProjectIds.flatMap(id => researchProjectCounts[id] || []));
        return;
      }
      (async () => {
        const fetchedData = await GetSampleTrackingCountsBySampleTypeForResearchProjects(missingProjects, accessToken);

        const updatedCounts = updateResearchProjectCounts(missingProjects, fetchedData);

        resolve(researchProjectIds.flatMap(id => updatedCounts[id] || []));
      })();
    });
  };

  // Fetches the sample tracking counts for a transition and returns the counts.
  // Will always fetch the latest counts from the server.
  const refreshSampleTrackingCountsForTransition = async (
    researchProjectId: string,
    configuredTransitionId: string
  ): Promise<ReadonlyArray<SampleTrackingCounts>> => {
    return new Promise<ReadonlyArray<SampleTrackingCounts>>(resolve => {
      if (!accessToken) {
        resolve(researchProjectCounts[researchProjectId] || []);
        return;
      }
      (async () => {
        const fetchedData = await GetSampleTrackingCountsBySampleTypeForTransition(
          researchProjectId,
          configuredTransitionId,
          accessToken
        );

        const updatedCounts = updateResearchProjectCountsForTransition(researchProjectId, fetchedData);
        resolve(updatedCounts[researchProjectId]);
      })();
    });
  };

  // Fetches the sample tracking counts for a project and returns the counts.
  // Will always fetch the latest counts from the server.
  const refreshSampleTrackingCountsForProject = async (
    researchProjectId: string
  ): Promise<ReadonlyArray<SampleTrackingCounts>> => {
    return new Promise<ReadonlyArray<SampleTrackingCounts>>(resolve => {
      if (!accessToken) {
        resolve(researchProjectCounts[researchProjectId] || []);
        return;
      }
      (async () => {
        const fetchedData = await GetSampleTrackingCountsBySampleType(researchProjectId, accessToken);

        const updatedCounts = updateResearchProjectCounts([researchProjectId], fetchedData);
        resolve(updatedCounts[researchProjectId]);
      })();
    });
  };

  const value = {
    researchProjectCounts,
    getSampleTrackingCountsForProject,
    getSampleTrackingCountsForAllProjects,
    refreshSampleTrackingCountsForTransition,
    refreshSampleTrackingCountsForProject,
  };

  return <SampleTrackingContext.Provider value={value}>{children}</SampleTrackingContext.Provider>;
};
